import { ChevronDownIcon, DownloadIcon, FileTextIcon, Loader2Icon } from 'components/icons';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { usePostHog } from '@posthog/react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { portalApiAxios } from 'store/api/portalAPI';
import { errorAlert } from 'utils/alert';
import { extractFilename, saveBlobAsFile } from 'utils/downloadFile';

type MeetingPrepDocumentFormat = 'pdf' | 'docx';

const DOCUMENT_FORMATS: Record<
    MeetingPrepDocumentFormat,
    { label: string; mimeType: string; downloadedEvent: string }
> = {
    pdf: { label: 'PDF', mimeType: 'application/pdf', downloadedEvent: 'meeting_prep_pdf_downloaded' },
    docx: {
        label: 'DOCX',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        downloadedEvent: 'meeting_prep_docx_downloaded',
    },
};

interface Props {
    /** The answer's id, which arrives once it has finished: until then the menu waits, disabled. */
    aiChatMessageId: number | undefined;
}

/**
 * Download the stored brief as a PDF or a DOCX, on the kit menu. While a file is built the button says so and keeps
 * its focus.
 */
const MeetingPrepDownloadButton: React.FC<Props> = ({ aiChatMessageId }) => {
    const [generatingFormat, setGeneratingFormat] = useState<MeetingPrepDocumentFormat | null>(null);
    const posthog = usePostHog();
    const intl = useIntl();

    const handleDownload = async (format: MeetingPrepDocumentFormat) => {
        if (!aiChatMessageId) return;
        setGeneratingFormat(format);
        try {
            const response = await portalApiAxios.post(`/v1/ai/meeting-prep/${format}/${aiChatMessageId}`, null, {
                responseType: 'blob',
            });

            const fileName =
                extractFilename(response.headers['content-disposition'] as string | undefined) ??
                `Meeting Prep.${format}`;

            saveBlobAsFile(new Blob([response.data], { type: DOCUMENT_FORMATS[format].mimeType }), fileName);
            posthog.capture(DOCUMENT_FORMATS[format].downloadedEvent);
        } catch {
            errorAlert(
                intl.formatMessage({
                    id: 'ai-chat.meeting-prep.download-failed',
                    defaultMessage: 'Could not generate the meeting prep document.',
                })
            );
        } finally {
            setGeneratingFormat(null);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button
                        variant="outline"
                        disabled={generatingFormat !== null || !aiChatMessageId}
                        focusableWhenDisabled={generatingFormat !== null}
                        className="text-meta border-border-control bg-card text-foreground hover:bg-muted h-8 gap-1.5 rounded-lg ps-2.5 pe-2 font-medium shadow-none aria-disabled:cursor-default"
                    />
                }
            >
                {generatingFormat ? (
                    <>
                        <Loader2Icon className="size-3.5 motion-safe:animate-spin motion-reduce:animate-pulse" />
                        <FormattedMessage
                            id="ai-chat.meeting-prep.downloading"
                            defaultMessage="Generating {format}…"
                            values={{ format: DOCUMENT_FORMATS[generatingFormat].label }}
                        />
                    </>
                ) : (
                    <>
                        <DownloadIcon className="size-3.5" />
                        <FormattedMessage id="ai-chat.meeting-prep.download" defaultMessage="Download" />
                        <ChevronDownIcon className="text-foreground-tertiary size-3.5 duration-(--duration-fast) ease-(--ease-smooth-out) group-aria-expanded/button:-scale-y-100 motion-safe:transition-transform" />
                    </>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
                {(['pdf', 'docx'] as const).map((format) => (
                    <DropdownMenuItem key={format} className="text-meta" onClick={() => void handleDownload(format)}>
                        <FileTextIcon className="text-foreground-tertiary size-4" />
                        {intl.formatMessage({ id: `ai-chat.meeting-prep.format-${format}` })}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default MeetingPrepDownloadButton;
